import GiftNFTIcon from "@/assets/icons/gift-nft.svg"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { networkAtom, refreshNFTVoucherListAtom } from "@/atoms/settings"
import { nativeTokenBalanceAtom } from "@/atoms/wallet"
import ClaimDialog from "@/components/dashboard/modal/modalNFT/components/ClaimDialog"
import { ModalTxConfirm } from "@/components/modalTxConfirm"
import { ModalTxResult } from "@/components/modalTxResult"
import { RelendModal } from "@/components/relendModal"
import { useFetchNftWallet } from "@/hooks/useFetchNFT"
import useLocalStorage from "@/hooks/useLocalStorage"
import { useNFTVoucher } from "@/hooks/useNFTVoucher"
import useTrans from "@/hooks/useTrans"
import { useTransactionHandler } from "@/hooks/useTransactionHandler"
import useWalletConnect from "@/hooks/useWalletConnect"
import { IS_MAINNET } from "@/utils/constants"
import { HelperText, TransactionDetail } from "@/utils/dataTypes"
import { sendEventLog } from "@/utils/tracking-events"
import { Button, Stack, Typography } from "@mui/material"
import { BlockchainNetwork, RENEC, SOL } from "@renec-foundation/relend-adapter"
import BN from "bn.js"
import { useAtomValue, useSetAtom } from "jotai"
import Image from "next/image"
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "react-query"

const ClaimNFTVoucher = () => {
  const trans = useTrans()
  const { wallet, address } = useWalletConnect()

  const [isClaimingNFT, setIsClaimingNFT] = useState(false)
  const [openConfirmClaimNFTModal, setOpenConfirmClaimNFTModal] = useState(false)
  const [openChooseClaimNFTModal, setOpenChooseClaimNFTModal] = useState(false)
  const [nftMintSelected, setNftMintSelected] = useState<string>()
  const [isShowClaimNFTRewardModal] = useLocalStorage("isShowClaimNFTRewardModal", false)
  const [isFirstShowRewardModal, setIsFirstShowRewardModal] = useLocalStorage("isFirstShowRewardModal", false)

  const { claimNFTVoucherWithCustomLogic, getAvailableNfts } = useNFTVoucher()
  const { handleClaimNftResult } = useTransactionHandler()

  const selectedObligation = useAtomValue(selectedMainObligationAtom)

  const [transactionDetail, setTransactionDetail] = useState<TransactionDetail>({
    isError: true,
    isActive: false,
    ctaButtonLink: "",
    title: "",
    content: "",
  })
  const [helperText, setHelperText] = useState<HelperText>({
    content: "",
    color: "",
  })
  const network = useAtomValue(networkAtom)
  const nativeTokenBalance = useAtomValue(nativeTokenBalanceAtom)
  const hasZeroBalance = !nativeTokenBalance || nativeTokenBalance === 0
  const nativeTokenName = network === BlockchainNetwork.RENEC ? RENEC : SOL

  // atoms
  const refreshNFTVoucherList = useSetAtom(refreshNFTVoucherListAtom)

  const { data: availableNfts, refetch: refetchAvailableNfts } = useQuery(
    ["fetchAvailableNfts", address, network],
    async () => {
      return await getAvailableNfts()
    },
    {
      staleTime: 10000,
    },
  )

  const { data: claimedCampaigns, refetch: refetchLendingNFT } = useFetchNftWallet(
    "",
    wallet?.adapter.publicKey || null,
  )

  useEffect(() => {
    if (address && isShowClaimNFTRewardModal && !isFirstShowRewardModal) {
      setOpenChooseClaimNFTModal(true)
      setIsFirstShowRewardModal(true)
    }
  }, [address, isShowClaimNFTRewardModal, isFirstShowRewardModal, setIsFirstShowRewardModal])

  const campaignId = useMemo(() => {
    if (!nftMintSelected) return

    const campaignSelected = availableNfts?.find((x) => x.mintAddress === nftMintSelected)
    if (!campaignSelected) return new BN(0)

    return new BN(campaignSelected.campaignId || 0)
  }, [nftMintSelected, availableNfts])

  const handleCloseChooseModal = useCallback(() => {
    setOpenChooseClaimNFTModal(false)
  }, [])

  const handleCloseClaimModal = useCallback(() => {
    if (!isClaimingNFT) {
      setTransactionDetail({
        isError: true,
        isActive: false,
        ctaButtonLink: "",
        title: "",
        content: "",
      })
      setHelperText({ content: "", color: "" })
      setOpenConfirmClaimNFTModal(false)
      setNftMintSelected(undefined)
    }
  }, [isClaimingNFT])

  const totalAvailableNfts = availableNfts ? availableNfts.length : 0
  const totalClaimedCampaigns = claimedCampaigns ? claimedCampaigns.length : 0
  const totalNFTVouchers = totalClaimedCampaigns + totalAvailableNfts

  const handleNFTItemClaimClick = useCallback(() => {
    setOpenConfirmClaimNFTModal(true)
  }, [])

  const handleOpenChooseModal = useCallback(() => {
    setOpenChooseClaimNFTModal(true)
  }, [])

  const handleSelectNFTChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNftMintSelected((event.target as HTMLInputElement).value)
  }, [])

  const handleClaimNFT = useCallback(
    async (campaignId: BN) => {
      try {
        if (hasZeroBalance) {
          const link =
            network === BlockchainNetwork.RENEC
              ? trans.dashboard.modal.not_enough_balance_link
              : trans.dashboard.modal.not_enough_balance_solana_token_link(nativeTokenName)
          window.open(link, "_blank")
          return
        }
        setIsClaimingNFT(true)
        const result = await claimNFTVoucherWithCustomLogic(campaignId)
        if (result.isSucceeded) {
          sendEventLog("user_claimed_reward_nft_success", {
            isMainnet: IS_MAINNET,
            walletAddress: address,
            obligationAddress: selectedObligation?.address,
          })
          setOpenConfirmClaimNFTModal(false)
        }
        handleClaimNftResult({
          result,
          trans,
          walletAddress: address,
          obligationAddress: selectedObligation?.address,
          setTransactionDetail,
          setHelperText,
        })
        refreshNFTVoucherList()
        refetchAvailableNfts()
        refetchLendingNFT()
      } catch (err) {
        console.error(err)
      } finally {
        setIsClaimingNFT(false)
      }
    },
    [
      hasZeroBalance,
      claimNFTVoucherWithCustomLogic,
      handleClaimNftResult,
      trans,
      address,
      selectedObligation?.address,
      refreshNFTVoucherList,
      refetchAvailableNfts,
      refetchLendingNFT,
      network,
      nativeTokenName,
    ],
  )

  const titleDialog = useMemo(() => {
    if (!transactionDetail.isActive) return

    return transactionDetail.isError ? trans.dashboard.modal.oops : trans.dashboard.modal.congrats
  }, [trans, transactionDetail])

  return (
    <>
      <Stack gap={0.5} data-testid='nft-voucher-claim-container'>
        <Typography className='text-sm text-neutral-600 flex gap-1 items-center'>
          {trans.dashboard.hero.total_nft_voucher}
        </Typography>

        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack>
            <Typography className='font-semibold text-base text-neutral-900'>
              {totalNFTVouchers}{" "}
              <span className='text-neutral-600 text-sm font-normal'>{trans.dashboard.hero.nft_voucher}</span>
            </Typography>
            <Typography
              className='text-sm text-neutral-600'
              dangerouslySetInnerHTML={{ __html: trans.dashboard.hero.available_to_claim(totalAvailableNfts) }}
            />
          </Stack>
          <Button
            onClick={handleOpenChooseModal}
            className='p-1 cursor-pointer text-xs hover:bg-transparent text-primary underline hover:underline hover:text-primary/80 disabled:opacity-60 disabled:text-primary z-10'
            data-testid='nft-voucher-view-detail-button'
          >
            <Image className='mr-1 inline' src={GiftNFTIcon} alt='click-icon' width={16} height={16} />
            {trans.dashboard.hero.nft_view_detail}
          </Button>
        </Stack>
      </Stack>

      {transactionDetail.isActive && (
        <RelendModal
          title={titleDialog}
          isSmallPopup={transactionDetail.isActive}
          hideButtonClose={transactionDetail.isActive}
          open={transactionDetail.isActive}
          handleClose={handleCloseClaimModal}
          data-testid='modal-claim-to-earn'
          useDrawerOnMobile
        >
          {transactionDetail.isActive && (
            <ModalTxResult transactionDetail={transactionDetail} handleCloseModal={handleCloseClaimModal} />
          )}
        </RelendModal>
      )}

      <ClaimDialog
        isOpen={openChooseClaimNFTModal}
        handleCloseModal={handleCloseChooseModal}
        availableClaimNFTs={availableNfts}
        claimedNFTs={claimedCampaigns}
        title={trans.dashboard.hero.title_nft_vouchers}
        nftMintSelected={nftMintSelected}
        handleSelectNFTChange={handleSelectNFTChange}
        handleClaimClick={handleNFTItemClaimClick}
        setNftMintSelected={setNftMintSelected}
        shouldRefreshCheckbox
      />

      {campaignId && (
        <RelendModal
          title={trans.dashboard.modal.confirm}
          isSmallPopup
          showCloseButton
          open={openConfirmClaimNFTModal}
          handleClose={handleCloseClaimModal}
          data-testid='modal-confirm-claim-nft'
          useDrawerOnMobile
          textButtonClose={trans.dashboard.modal.claim_later}
          zIndex={2000}
        >
          <ModalTxConfirm
            title={trans.dashboard.modal.claim_your_reward}
            description={trans.dashboard.modal.claim_nft_voucher_desc}
            helperText={helperText}
            textConfirm={
              hasZeroBalance
                ? trans.dashboard.modal.errors.insufficient_balance(nativeTokenName)
                : trans.dashboard.modal.claim_confirm
            }
            loading={isClaimingNFT}
            handleConfirm={() => handleClaimNFT(campaignId)}
          />
        </RelendModal>
      )}
    </>
  )
}

export default ClaimNFTVoucher
